// Libs
import classNames from 'classnames/bind';
import { IconRefreshLine } from '@instructure/ui-icons';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Heading, Spinner, Text, View } from '@instructure/ui';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import ReplyInput from './component/ReplyInput/ReplyInput';
import DiscussionEntry from './component/DiscussionEntry/DiscussionEntry';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getDetailDiscussion, getFullDiscussionEntries, markReadAllEntries } from '~/thunks/discussion/discussionThunk';
import { getFileIconByMimetype } from '~/utils/helper/common';
import { IDiscussionEntry } from '~/utils/interface/discussion';
import { TIMEOUT_REFRESH_DISCUSSION } from '~/utils/constants/common';
// Styles, images, icons
import styles from './DiscussionDetail.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const CourseDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/discussion', 'common/base']);
  const dispatch = useAppDispatch();
  const { courseId, discussionId } = useParams();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  const { discussionDetail, isLoading, errorMessage, discussionEntries, isRefreshList } = useAppSelector(
    (state) => state.discussionState
  );
  //#endregion Selector

  //#region Declare State
  const isFullContainer = (isLoading && !discussionDetail) || errorMessage;
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetDetailDiscussion();
    handleGetDiscussionEntries();
    handleMarkReadAllEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTimeout(() => {
      handleGetDetailDiscussion();
      handleGetDiscussionEntries();
    }, TIMEOUT_REFRESH_DISCUSSION);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshList]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailDiscussion = async () => {
    if (!courseId || !discussionId) return;

    dispatch(getDetailDiscussion({ courseId: courseId, discussionId: discussionId })).unwrap();
  };

  const handleGetDiscussionEntries = async () => {
    if (!courseId || !discussionId) return;

    dispatch(getFullDiscussionEntries({ courseId: courseId, discussionId: discussionId })).unwrap();
  };

  const handleMarkReadAllEntries = async () => {
    if (!courseId || !discussionId) return;

    dispatch(markReadAllEntries({ courseId: courseId, discussionId: discussionId })).unwrap();
  };

  const countDiscussionEntries = (entries: IDiscussionEntry[]): number => {
    let total = 0;

    const dfs = (entry: IDiscussionEntry) => {
      total += 1;
      if (entry.replies && entry.replies.length > 0) {
        entry.replies.forEach(dfs);
      }
    };

    entries.forEach(dfs);

    return total;
  };

  const renderDiscussionDetail = () => {
    return (
      <div className={cx('discussionContainer')}>
        <div className={cx('header')}>
          <img
            src={discussionDetail?.author?.avatarUrl}
            alt={t('common_alt_img', { ns: 'common/base' })}
            className={cx('avatar')}
          />

          <div className={cx('headerInformation')}>
            <View margin='x-small 0 0 0'>
              <Text size='large'>{discussionDetail?.title}</Text>
            </View>
            <Text size='x-small'>{discussionDetail?.author?.name}</Text>
          </div>
        </div>

        {discussionDetail?.message && <div dangerouslySetInnerHTML={{ __html: discussionDetail?.message }} />}

        {discussionDetail?.attachments && discussionDetail?.attachments.length > 0 && (
          <div className={cx('attachmentContainer')}>
            {discussionDetail?.attachments.map((attachment, index) => {
              const FileIcon = getFileIconByMimetype(attachment.mimetype);
              return (
                <div key={index} className={cx('attachmentItem')}>
                  <FileIcon />
                  <a href={attachment.url}>{attachment.displayName}</a>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderDiscussionComment = () => {
    return (
      <div className={cx('commentContainer')}>
        {discussionDetail?.permissions?.reply && (
          <div className={cx('postCommentContainer')}>
            <ReplyInput courseId={courseId} discussionId={discussionId} />
          </div>
        )}

        <Heading level='h3' margin='small 0 large 0'>
          {t('discussion_comment_label')}
          <Badge standalone count={countDiscussionEntries(discussionEntries)} margin='0 0 0 small' />
        </Heading>

        {[...discussionEntries].reverse().map((entry, index) => (
          <DiscussionEntry key={index} courseId={courseId} discussionId={discussionId} data={entry} />
        ))}
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={cx('centerContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={cx('centerContainer')}>
          <Button onClick={handleGetDetailDiscussion} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
            {t('retry_button_label', { ns: 'common/base' })}
          </Button>
        </div>
      );
    }

    return (
      <>
        {renderDiscussionDetail()}

        {renderDiscussionComment()}
      </>
    );
  };

  const handleBack = () => {
    navigate(-1);
  };
  //#endregion Handle Function

  return (
    <div className={cx('container', isFullContainer && 'fullContainer')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('content')}>{renderContent()}</div>
    </div>
  );
};

export default CourseDetail;
